import React, {ReactNode} from 'react'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import { Box, Table, TableBody, TableCell, TableRow, InputAdornment } from '@mui/material';
import {
    AVAILABLE_OR_NOT,
    CLEANING_TIME,
    HOURS,
    NUMBER_OF_TIMES_TO_STAY, RENTAL_TYPE,
    RESERVATION_TIME_UNIT_MINUTES
} from '../../../CodeMaster'
import PPNumericField from '../../shared/forms/PPNumericField'
import PPCheckboxGroup from '../../shared/forms/PPCheckboxGroup'
import {Messages} from '../../../Messages'
import PPSelect from '../../shared/forms/PPSelect'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {HKConstants} from '../../../HKConstants'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledLabel} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {addEmptyData, isNoSelect, valueOrNoSelected} from '../../../componentHelper/selectHelper'
import {numberOrNull, stringOrEmpty} from '../../../stringHelper/stringHelpers'
import equal from 'fast-deep-equal'
import EventEmitterExAdmin from '../../shared/pageBaseComponents/EventEmitterExAdmin'
import PPErrorLabel from '../../shared/forms/PPErrorLabel'
import FacilityOptionSetting from './FacilityOptionSetting/FacilityOptionSetting'
import {OptionSetting} from './FacilityOptionSetting/FacilityOptionInputForm'
import HintButton from '../../shared/pageBaseComponents/HintButton'
import {SelectChangeEvent} from '@mui/material/Select'

export interface StaySettings {
    stayPossibleDateCount: Nullable<NUMBER_OF_TIMES_TO_STAY>
    stayContinueTimeRental: AVAILABLE_OR_NOT
    checkinTimeHours: Nullable<HOURS>
    checkinTimeMinutes: Nullable<RESERVATION_TIME_UNIT_MINUTES>
    checkoutTimeHours: Nullable<HOURS>
    checkoutTimeMinutes: Nullable<RESERVATION_TIME_UNIT_MINUTES>
    stayCharge: Nullable<number>
}

interface Props extends StaySettings {
    stayAvailability: RENTAL_TYPE
    eventEmitter: EventEmitterExAdmin
    handleStaySettingsChange: (staySettings: StaySettings) => void
    optionSettings: OptionSetting[]
    reserveBeforeCleaningTime: Nullable<CLEANING_TIME>
    reserveAfterCleaningTime: Nullable<CLEANING_TIME>
    onUpdateOptions: (optionSettings: OptionSetting[]) => void
    onBeforeCleaningTimeChange: (cleaningTime: Nullable<CLEANING_TIME>) => void
    onAfterCleaningTimeChange: (cleaningTime: Nullable<CLEANING_TIME>) => void
}

class StaySettingsComponent extends React.Component<Props> {

    static displayName: string = 'StaySettingsComponent'

    constructor(props: Readonly<Props>) {
        super(props)

        this.handleChangeStayContinueTimeRental = this.handleChangeStayContinueTimeRental.bind(this)
        this.handleChangeStayPossibleDataCount = this.handleChangeStayPossibleDataCount.bind(this)
        this.handleChangeCheckinTimeHours = this.handleChangeCheckinTimeHours.bind(this)
        this.handleChangeCheckinTimeMinutes = this.handleChangeCheckinTimeMinutes.bind(this)
        this.handleChangeCheckoutTimeHours = this.handleChangeCheckoutTimeHours.bind(this)
        this.handleChangeCheckoutTimeMinutes = this.handleChangeCheckoutTimeMinutes.bind(this)
        this.handleChangeStayCharge = this.handleChangeStayCharge.bind(this)
        this.handleUpdateOptionSettings = this.handleUpdateOptionSettings.bind(this)
        this.handleReserveBeforeCleaningTimeChange = this.handleReserveBeforeCleaningTimeChange.bind(this)
        this.handleReserveAfterCleaningTimeChange = this.handleReserveAfterCleaningTimeChange.bind(this)
    }

    shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
        return !(this.props === nextProps || equal(this.props, nextProps))
    }

    handleUpdateOptionSettings(optionSettings: OptionSetting[]): void {
        this.props.onUpdateOptions(optionSettings)
    }

    handleReserveBeforeCleaningTimeChange(event: SelectChangeEvent<unknown>): void {
        const cleaningTime: Nullable<CLEANING_TIME> = this.toCleaningTime(event.target.value)
        this.props.onBeforeCleaningTimeChange(cleaningTime)
    }

    handleReserveAfterCleaningTimeChange(event: SelectChangeEvent<unknown>): void {
        const cleaningTime: Nullable<CLEANING_TIME> = this.toCleaningTime(event.target.value)
        this.props.onAfterCleaningTimeChange(cleaningTime)
    }

    private toCleaningTime(cleaningTime: unknown): Nullable<CLEANING_TIME> {
        return isNoSelect(cleaningTime) ? null : CLEANING_TIME.codeToEnumConstants(`${cleaningTime}`)
    }

    handleChangeStayContinueTimeRental(
        event: React.ChangeEvent<HTMLInputElement>,
    ): void {
        const {
            handleStaySettingsChange,
            eventEmitter,
            onAfterCleaningTimeChange,
            onBeforeCleaningTimeChange,
            onUpdateOptions,
            optionSettings,
            reserveAfterCleaningTime,
            reserveBeforeCleaningTime,
            stayAvailability,
            ...rest
        } = this.props
        const stayContinueTimeRental: AVAILABLE_OR_NOT =
            (event.target.checked) ? AVAILABLE_OR_NOT.AVAILABLE : AVAILABLE_OR_NOT.UNAVAILABLE

        handleStaySettingsChange({
            ...rest,
            stayContinueTimeRental,
        })
    }

    handleChangeStayPossibleDataCount(event: SelectChangeEvent<unknown>): void {
        const {
            handleStaySettingsChange,
            eventEmitter,
            onAfterCleaningTimeChange,
            onBeforeCleaningTimeChange,
            onUpdateOptions,
            optionSettings,
            reserveAfterCleaningTime,
            reserveBeforeCleaningTime,
            stayAvailability,
            ...rest
        } = this.props
        const value: string = `${event.target.value}`
        handleStaySettingsChange({
            ...rest,
            stayPossibleDateCount: isNoSelect(value) ? null : NUMBER_OF_TIMES_TO_STAY.codeToEnumConstants(value),
        })
    }

    handleChangeCheckinTimeHours(event: SelectChangeEvent<unknown>): void {
        const {
            handleStaySettingsChange,
            eventEmitter,
            onAfterCleaningTimeChange,
            onBeforeCleaningTimeChange,
            onUpdateOptions,
            optionSettings,
            reserveAfterCleaningTime,
            reserveBeforeCleaningTime,
            stayAvailability,
            ...rest
        } = this.props
        const value: string = `${event.target.value}`
        handleStaySettingsChange({
            ...rest,
            checkinTimeHours: isNoSelect(value) ? null : HOURS.codeToEnumConstants(value),
        })
    }

    handleChangeCheckinTimeMinutes(event: SelectChangeEvent<unknown>): void {
        const {
            handleStaySettingsChange,
            eventEmitter,
            onAfterCleaningTimeChange,
            onBeforeCleaningTimeChange,
            onUpdateOptions,
            optionSettings,
            reserveAfterCleaningTime,
            reserveBeforeCleaningTime,
            stayAvailability,
            ...rest
        } = this.props
        const value: string = `${event.target.value}`
        handleStaySettingsChange({
            ...rest,
            checkinTimeMinutes: isNoSelect(value) ? null : RESERVATION_TIME_UNIT_MINUTES.codeToEnumConstants(value),
        })
    }

    handleChangeCheckoutTimeHours(event: SelectChangeEvent<unknown>): void {
        const {
            handleStaySettingsChange,
            eventEmitter,
            onAfterCleaningTimeChange,
            onBeforeCleaningTimeChange,
            onUpdateOptions,
            optionSettings,
            reserveAfterCleaningTime,
            reserveBeforeCleaningTime,
            stayAvailability,
            ...rest
        } = this.props
        const value: string = `${event.target.value}`
        handleStaySettingsChange({
            ...rest,
            checkoutTimeHours: isNoSelect(value) ? null : HOURS.codeToEnumConstants(value),
        })
    }

    handleChangeCheckoutTimeMinutes(event: SelectChangeEvent<unknown>): void {
        const {
            handleStaySettingsChange,
            eventEmitter,
            onAfterCleaningTimeChange,
            onBeforeCleaningTimeChange,
            onUpdateOptions,
            optionSettings,
            reserveAfterCleaningTime,
            reserveBeforeCleaningTime,
            stayAvailability,
            ...rest
        } = this.props
        const value: string = `${event.target.value}`
        handleStaySettingsChange({
            ...rest,
            checkoutTimeMinutes: isNoSelect(value) ? null : RESERVATION_TIME_UNIT_MINUTES.codeToEnumConstants(value),
        })
    }

    handleChangeStayCharge(event: React.ChangeEvent<{ name?: string; value: unknown }>): void {
        const {
            handleStaySettingsChange,
            eventEmitter,
            onAfterCleaningTimeChange,
            onBeforeCleaningTimeChange,
            onUpdateOptions,
            optionSettings,
            reserveAfterCleaningTime,
            reserveBeforeCleaningTime,
            stayAvailability,
            ...rest
        } = this.props
        handleStaySettingsChange({
            ...rest,
            stayCharge: numberOrNull(`${event.target.value}`),
        })
    }

    renderStaySettings(): ReactNode {
        return (<>
            <TableRow>
                <TableRowName required>宿泊可能日数</TableRowName>
                <TableCell>
                    <LeftRowFlexContainer>
                        <StayPossibleDateCountLabel>
                            <LeftRowFlexContainer>
                                <PPSelect
                                    data={addEmptyData(NUMBER_OF_TIMES_TO_STAY.values())}
                                    value={valueOrNoSelected(this.props.stayPossibleDateCount)}
                                    onChange={this.handleChangeStayPossibleDataCount}
                                />
                                日まで
                            </LeftRowFlexContainer>
                        </StayPossibleDateCountLabel>
                        <Box width={'40px'}/>
                        <StayPossibleDateCountLabel>
                            連泊中時間貸
                        </StayPossibleDateCountLabel>
                        <StayPossibleDateCountLabel>
                            <LeftRowFlexContainer>
                                <PPCheckboxGroup
                                    row
                                    data={[{
                                        label: AVAILABLE_OR_NOT.AVAILABLE.label,
                                        checked: this.props.stayContinueTimeRental
                                            === AVAILABLE_OR_NOT.AVAILABLE,
                                    }]}
                                    onChange={this.handleChangeStayContinueTimeRental}
                                    hint={Messages.HFACA0002.toString()}
                                />
                            </LeftRowFlexContainer>
                        </StayPossibleDateCountLabel>
                    </LeftRowFlexContainer>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableRowName required>チェックインアウト</TableRowName>
                <TableCell>
                    <LeftRowFlexContainer>
                        <CheckinoutLabel>
                            <LeftRowFlexContainer>
                                <PPSelect
                                    data={addEmptyData(HOURS.values())}
                                    value={valueOrNoSelected(this.props.checkinTimeHours)}
                                    onChange={this.handleChangeCheckinTimeHours}
                                />
                                時
                            </LeftRowFlexContainer>
                        </CheckinoutLabel>
                        <CheckinoutLabel>
                            <LeftRowFlexContainer>
                                <PPSelect
                                    data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES.values())}
                                    value={valueOrNoSelected(this.props.checkinTimeMinutes)}
                                    onChange={this.handleChangeCheckinTimeMinutes}
                                />
                                分
                            </LeftRowFlexContainer>
                        </CheckinoutLabel>
                        <CheckinoutLabel>〜</CheckinoutLabel>
                        <CheckinoutLabel>
                            <LeftRowFlexContainer>
                                <PPSelect
                                    data={addEmptyData(HOURS.values())}
                                    value={valueOrNoSelected(this.props.checkoutTimeHours)}
                                    onChange={this.handleChangeCheckoutTimeHours}
                                />
                                時
                            </LeftRowFlexContainer>
                        </CheckinoutLabel>
                        <CheckinoutLabel>
                            <LeftRowFlexContainer>
                                <PPSelect
                                    data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES.values())}
                                    value={valueOrNoSelected(this.props.checkoutTimeMinutes)}
                                    onChange={this.handleChangeCheckoutTimeMinutes}
                                />
                                分
                            </LeftRowFlexContainer>
                        </CheckinoutLabel>
                        <PPErrorLabel
                            eventEmitter={this.props.eventEmitter}
                            eventKeys={['staySetting.checkoutTime']}
                        />
                    </LeftRowFlexContainer>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableRowName>掃除の時間</TableRowName>
                <TableCell>
                    <LeftRowFlexContainer>
                        <label>
                            <LeftRowFlexContainer>
                                予約前
                                <PPSelect
                                    data={addEmptyData(CLEANING_TIME.values())}
                                    value={valueOrNoSelected(this.props.reserveBeforeCleaningTime)}
                                    onChange={this.handleReserveBeforeCleaningTimeChange}
                                />
                            </LeftRowFlexContainer>
                        </label>
                        <Box marginLeft={'30px'}/>
                        <label>
                            <LeftRowFlexContainer>
                                予約後
                                <PPSelect
                                    data={addEmptyData(CLEANING_TIME.values())}
                                    value={valueOrNoSelected(this.props.reserveAfterCleaningTime)}
                                    onChange={this.handleReserveAfterCleaningTimeChange}
                                />
                                分
                            </LeftRowFlexContainer>
                        </label>
                        <HintButton message={Messages.HFACA0004.message}/>
                    </LeftRowFlexContainer>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableRowName required>料金</TableRowName>
                <TableCell>
                    <label>
                        <StayChargeInput
                            InputProps={{
                                inputProps: {
                                    min: 0,
                                    max: HKConstants.VMN.FAC.MAX_PRICE,
                                },
                                endAdornment: <InputAdornment position="end">円</InputAdornment>,
                            }}
                            value={stringOrEmpty(this.props.stayCharge)}
                            onChange={this.handleChangeStayCharge}
                        />
                    </label>
                </TableCell>
            </TableRow>
        </>)
    }

    render(): ReactNode {
        return (<>
            <Box marginTop={'20px'}>
                <Table>
                    <StyledTableCaption>宿泊施設の設定</StyledTableCaption>
                    <TableBody>
                        <TableRow>
                            <TableRowName>オプション設定</TableRowName>
                            <TableCell>
                                <FacilityOptionSetting
                                    options={this.props.optionSettings}
                                    stayAvailability={this.props.stayAvailability}
                                    eventEmitter={this.props.eventEmitter}
                                    onUpdateOptions={this.handleUpdateOptionSettings}
                                />
                            </TableCell>
                        </TableRow>
                        {this.renderStaySettings()}
                    </TableBody>
                </Table>
            </Box>
        </>)
    }
}

const StayPossibleDateCountLabel: StyledLabel = styled.label`
    margin-right: 30px;
`

const CheckinoutLabel: StyledLabel = styled.label`
    margin-right: 20px;
`

const StayChargeInput = styled(PPNumericField)`
  width: 110px;
`

export default StaySettingsComponent
